import {DragAndDrop, type OnDropArgs, useDragAndDrop, useSortable} from '@github-ui/drag-and-drop'
import {EmojiAutocomplete} from '@github-ui/emoji-autocomplete'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {UnsafeHTMLText} from '@github-ui/safe-html/UnsafeHTML'
import {testIdProps} from '@github-ui/test-id-props'
import {useIgnoreKeyboardActionsWhileComposing} from '@github-ui/use-ignore-keyboard-actions-while-composing'
import {CheckIcon, GrabberIcon, KebabHorizontalIcon, PencilIcon, XIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, Flash, FormControl, IconButton, Label, TextInput} from '@primer/react'
import {clsx} from 'clsx'
import {useRef, useState} from 'react'

import {replaceShortCodesWithEmojis} from './emojis'
import {useReturnFocus} from './hooks/use-return-focus'
import {Resources} from './resources'
import styles from './SingleSelectForm.module.css'
import {SingleSelectOptionModal} from './SingleSelectOptionModal'
import {SingleSelectToken} from './SingleSelectToken'
import {
  emptySingleSelectOption,
  type NewSingleSelectOption,
  type PersistedSingleSelectOption,
  type SingleSelectOption,
  type SingleSelectOptionWithId,
} from './types'
import {getOptionNameValidationErrors, isValidForSubmission} from './util'

export interface SingleSelectFormProps {
  options: SingleSelectOptionWithId[]
  canRemoveOption: boolean
  maxNumberOfOptions: number
  onAddOption: (option: NewSingleSelectOption) => void
  onUpdateOption: (updatedOption: SingleSelectOptionWithId) => void
  onRemoveOption: (id: string) => void
  onDrop: ({dragMetadata, dropMetadata, isBefore}: OnDropArgs<string>) => void
  className?: string
  // If true, will not allow duplicated options. Once memex starts using this enforcement, we can remove this prop. It
  // is here for compatibility with existing code.
  noDuplicatedOptions: boolean
  disabled?: boolean
  /**
   * Enable length validation for descriptions in the modal (edit/create) to allow users to
   * type beyond the 100 char limit and see validation errors. Submission remains blocked while over the limit.
   * Inline creation still enforces the limit. Intended for Issue Field Settings only.
   */
  enableDescriptionLengthValidation: boolean
  /**
   * Enable length validation for names in the modal (edit/create) to allow users to
   * type beyond the 50 char limit and see validation errors. Submission remains blocked while over the limit.
   * Inline creation still enforces the limit. Intended for Issue Field Settings only.
   */
  enableNameLengthValidation: boolean
  showOptionsBorder?: boolean
  /**
   * Function used to preprocess the SingleSelectOption name before it is assigned to nameHtml for the SingleSelectOptionModal preview.
   * The result is then sanitized by UnsafeHTMLText using DOMPurify to prevent XSS attacks.
   * If not provided, a strict default sanitizer is used that prohibits most HTML tags, attributes, and styling.
   */
  preSanitizeHtml?: (value: string) => SafeHTMLString
  /**
   * The ID of the option to use as the default value.
   */
  defaultValueId?: string
  /**
   * Callback when the default value is changed.
   */
  onSetDefaultValue?: (optionId: string | undefined) => void
}

export const SingleSelectForm = ({
  options,
  canRemoveOption,
  maxNumberOfOptions,
  onAddOption: external_onAddOption,
  onUpdateOption,
  onRemoveOption,
  onDrop,
  className,
  noDuplicatedOptions = false,
  disabled = false,
  enableNameLengthValidation = false,
  enableDescriptionLengthValidation = false,
  showOptionsBorder = false,
  preSanitizeHtml,
  defaultValueId,
  onSetDefaultValue,
}: SingleSelectFormProps) => {
  const dropRef = useRef<HTMLOListElement>(null)
  const optionsLimitReached = options.length >= maxNumberOfOptions

  const selectInputByIndex = (index: number) =>
    dropRef.current?.querySelector<HTMLInputElement>(`input[data-option-index='${index}']`)

  const onAddOption = (option: NewSingleSelectOption = emptySingleSelectOption) => {
    external_onAddOption(option)
    setTimeout(() => selectInputByIndex(options.length)?.focus())
  }

  const columnOptions = options.slice(0, maxNumberOfOptions)
  const showOptionsBox = Boolean(showOptionsBorder && columnOptions.length > 0)

  return (
    <div className={clsx(styles.SingleSelectFormContainer, disabled && styles.DisabledForm)}>
      <div className={clsx(styles.SingleSelectFormList, className)}>
        <div className={clsx(showOptionsBox && styles.SingleSelectFormOptionContainer)}>
          <DragAndDrop
            items={columnOptions.map(option => {
              return {id: option.id, title: option.name, option}
            })}
            onDrop={onDrop}
            style={{listStyle: 'none'}}
            aria-label={'Single select options'}
            renderOverlay={({option}, index) => (
              <DragAndDrop.Item
                id={option.id}
                index={index}
                title={option.name}
                key={option.id}
                hideTrigger
                isDragOverlay
              >
                <SingleSelectItem
                  key={option.id}
                  option={option}
                  index={index}
                  enableOptionRemoval={canRemoveOption && !disabled}
                  onRemove={() => onRemoveOption(option.id)}
                  onChange={onUpdateOption}
                  noDuplicatedOptions={noDuplicatedOptions}
                  options={options}
                  disabled={disabled}
                  enableNameLengthValidation={enableNameLengthValidation}
                  enableDescriptionLengthValidation={enableDescriptionLengthValidation}
                  preSanitizeHtml={preSanitizeHtml}
                  isDefault={defaultValueId === option.id}
                  onSetDefaultValue={onSetDefaultValue}
                />
              </DragAndDrop.Item>
            )}
          >
            {options.slice(0, maxNumberOfOptions).map((option, index) => (
              <DragAndDrop.Item id={option.id} index={index} title={option.name} key={option.id} hideTrigger>
                <SingleSelectItem
                  key={option.id}
                  option={option}
                  index={index}
                  enableOptionRemoval={canRemoveOption}
                  onRemove={() => onRemoveOption(option.id)}
                  onChange={onUpdateOption}
                  noDuplicatedOptions={noDuplicatedOptions}
                  options={options}
                  disabled={disabled}
                  enableNameLengthValidation={enableNameLengthValidation}
                  enableDescriptionLengthValidation={enableDescriptionLengthValidation}
                  preSanitizeHtml={preSanitizeHtml}
                  isDefault={defaultValueId === option.id}
                  onSetDefaultValue={onSetDefaultValue}
                />
              </DragAndDrop.Item>
            ))}
          </DragAndDrop>
        </div>
        {!disabled && (
          <div
            className={clsx(styles.SingleSelectFormAddOptionContainer)}
            {...testIdProps('single-select-form-add-option-container')}
          >
            {optionsLimitReached ? (
              <Flash variant="warning" className={styles.SingleSelectFormWarningFlash}>
                {Resources.singleSelectOptionLimitWarning(maxNumberOfOptions)}
              </Flash>
            ) : (
              <NewItemCreator
                noDuplicatedOptions={noDuplicatedOptions}
                options={options}
                onCreate={onAddOption}
                enableNameLengthValidation={enableNameLengthValidation}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

interface NewItemCreatorProps {
  onCreate: (option?: SingleSelectOption) => void
  noDuplicatedOptions: boolean
  options: SingleSelectOptionWithId[]
  enableNameLengthValidation: boolean
}

const NewItemCreator = ({onCreate, noDuplicatedOptions, options, enableNameLengthValidation}: NewItemCreatorProps) => {
  const [name, setName] = useState('')

  const errors = getOptionNameValidationErrors(name, options, noDuplicatedOptions, enableNameLengthValidation)
  const isValid = isValidForSubmission(name, options, noDuplicatedOptions, enableNameLengthValidation)

  const onSubmit = () => {
    if (isValid) {
      onCreate({...emptySingleSelectOption, name: replaceShortCodesWithEmojis(name)})
      setName('')
    }
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (event.key === 'Enter') {
      event.preventDefault()
      onSubmit()
    }
  }
  const inputProps = useIgnoreKeyboardActionsWhileComposing(onKeyDown)

  const handleOnChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const currentValue = ev.target.value
    setName(currentValue)
  }

  const duplicateError = errors.find(error => error.type === 'duplicate')
  const tooLongError = errors.find(error => error.type === 'nameTooLong')

  return (
    <fieldset className={styles.SingleSelectFormAddOptionFieldset} onSubmit={onSubmit}>
      <legend className="sr-only">Add option</legend>
      <FormControl className={styles.SingleSelectFormAddOptionFormControl}>
        <FormControl.Label visuallyHidden>Label text</FormControl.Label>
        <EmojiAutocomplete fullWidth>
          <TextInput
            block
            value={name}
            onChange={handleOnChange}
            placeholder="Add option…"
            autoComplete="false"
            {...inputProps}
          />
        </EmojiAutocomplete>
        {duplicateError && <FormControl.Validation variant="error">{duplicateError.message}</FormControl.Validation>}
        {tooLongError && <FormControl.Validation variant="error">{tooLongError.message}</FormControl.Validation>}
      </FormControl>
      <Button onClick={onSubmit} disabled={!isValid}>
        Add
      </Button>
    </fieldset>
  )
}

interface SingleSelectItemProps {
  option: PersistedSingleSelectOption | SingleSelectOptionWithId
  index: number
  enableOptionRemoval: boolean
  onRemove: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void
  onChange: (updatedOption: SingleSelectOptionWithId) => void
  noDuplicatedOptions: boolean
  options: SingleSelectOptionWithId[]
  disabled?: boolean
  enableNameLengthValidation: boolean
  enableDescriptionLengthValidation: boolean
  preSanitizeHtml?: (value: string) => SafeHTMLString
  isDefault?: boolean
  onSetDefaultValue?: (optionId: string | undefined) => void
}

function SingleSelectItem({
  index,
  option,
  enableOptionRemoval,
  onRemove,
  onChange,
  noDuplicatedOptions,
  options,
  disabled = false,
  enableNameLengthValidation,
  enableDescriptionLengthValidation,
  preSanitizeHtml,
  isDefault = false,
  onSetDefaultValue,
}: SingleSelectItemProps) {
  const {setNodeRef} = useSortable({
    id: option.id,
    data: {metadata: {id: option.id}},
  })

  const [editModalVisible, setEditModalVisible] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const editButtonRef = useRef<HTMLButtonElement>(null)
  const {openMoveDialog} = useDragAndDrop()

  useReturnFocus(editModalVisible, editButtonRef)

  const description = 'descriptionHtml' in option ? option.descriptionHtml : option.description
  return (
    <div key={option.id} ref={setNodeRef} className={styles.SingleSelectFormItem} {...testIdProps('singleSelectItem')}>
      {!disabled && <DragAndDrop.DragTrigger />}
      <div className={styles.SingleSelectFormOptionContent}>
        <SingleSelectToken option={option} disabled={disabled} />
        {isDefault && (
          <Label variant="attention" {...testIdProps('default-option-label')}>
            Default
          </Label>
        )}
      </div>
      <UnsafeHTMLText className={styles.SingleSelectFormOptionDescription} as="p" html={description} />

      {!disabled && (
        <ActionMenu onOpenChange={setMenuOpen} open={menuOpen} anchorRef={editButtonRef}>
          <ActionMenu.Anchor>
            <IconButton
              icon={KebabHorizontalIcon}
              variant="invisible"
              aria-label={`Open field actions for ${option.name}`}
              tooltipDirection="n"
              size="small"
              className={clsx(styles.SingleSelectFormMenuButton)}
              {...testIdProps('single-select-item-menu-button')}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>
              <ActionList.Item
                onSelect={() => setEditModalVisible(true)}
                aria-label={`Open edit dialog for ${option.name}`}
                {...testIdProps('single-select-item-edit-button')}
              >
                <ActionList.LeadingVisual>
                  <PencilIcon />
                </ActionList.LeadingVisual>
                Edit option
              </ActionList.Item>
              <ActionList.Item
                aria-label={`Advanced move ${option.name}...`}
                onSelect={() => openMoveDialog(option.name, index, editButtonRef)}
                {...testIdProps('single-select-item-move-button')}
              >
                <ActionList.LeadingVisual>
                  <GrabberIcon />
                </ActionList.LeadingVisual>
                Advanced Move...
              </ActionList.Item>
              {onSetDefaultValue && (
                <>
                  <ActionList.Divider />
                  <ActionList.Item
                    onSelect={() => {
                      onSetDefaultValue(isDefault ? undefined : option.id)
                      setMenuOpen(false)
                    }}
                    aria-label={isDefault ? `Unset ${option.name} as default` : `Set ${option.name} as default`}
                    {...testIdProps(
                      isDefault ? 'single-select-item-unset-default-button' : 'single-select-item-set-default-button',
                    )}
                  >
                    <ActionList.LeadingVisual>
                      <CheckIcon />
                    </ActionList.LeadingVisual>
                    <span>{isDefault ? 'Unset as default' : 'Set as default'}</span>
                  </ActionList.Item>
                </>
              )}
              {enableOptionRemoval && (
                <>
                  <ActionList.Divider />
                  <ActionList.Item
                    variant="danger"
                    onSelect={onRemove}
                    aria-label={`Remove ${option.name}`}
                    {...testIdProps('single-select-item-delete-button')}
                  >
                    <ActionList.LeadingVisual>
                      <XIcon />
                    </ActionList.LeadingVisual>
                    Remove option
                  </ActionList.Item>
                </>
              )}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}

      {editModalVisible && (
        <SingleSelectOptionModal
          initialOption={option}
          onCancel={() => setEditModalVisible(false)}
          onSave={updatedOption => {
            setEditModalVisible(false)
            onChange({...option, ...updatedOption})
          }}
          options={options}
          noDuplicatedOptions={noDuplicatedOptions}
          enableNameLengthValidation={enableNameLengthValidation}
          enableDescriptionLengthValidation={enableDescriptionLengthValidation}
          preSanitizeHtml={preSanitizeHtml}
        />
      )}
    </div>
  )
}
